import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";

const NAV_ITEMS = [
    { label: "Platform", href: "#what" },
    { label: "Features", href: "#features" },
    { label: "How it works", href: "#how" },
    { label: "Artists", href: "#artists" },
];

export const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <motion.header
            initial={{ y: -30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="fixed top-3 md:top-4 left-0 right-0 z-50 flex justify-center px-3 md:px-4"
            data-testid="main-navbar"
        >
            <div
                className={`w-full max-w-6xl flex items-center justify-between gap-4 md:gap-6 h-14 md:h-16 px-3 md:px-6 rounded-[18px] md:rounded-[22px] border border-white/15 backdrop-blur-xl transition-colors duration-300 ${
                    scrolled ? "bg-[#030023]/80" : "bg-[#030023]/55"
                }`}
            >
                <a
                    href="#top"
                    className="flex items-center"
                    data-testid="nav-logo-link"
                >
                    <Logo size="default" />
                </a>

                <nav className="hidden lg:flex items-center gap-10">
                    {NAV_ITEMS.map((item) => (
                        <a
                            key={item.label}
                            href={item.href}
                            className="text-sm font-medium text-white/70 hover:text-white transition-colors tracking-wide"
                            data-testid={`nav-${item.label.replace(/\s/g, "-")}`}
                        >
                            {item.label}
                        </a>
                    ))}
                </nav>

                <div className="hidden lg:flex items-center gap-3">
                    <a
                        href="#cta"
                        className="text-sm font-medium text-white/80 hover:text-white px-5 py-2 transition-colors"
                        data-testid="nav-signin"
                    >
                        Sign in
                    </a>
                    <a
                        href="#cta"
                        className="text-sm font-semibold bg-[#791EE2] hover:bg-[#8b32f0] text-white px-6 py-3 rounded-full transition-all hover:shadow-[0_0_30px_rgba(121,30,226,0.5)] active:scale-95"
                        data-testid="nav-join-btn"
                    >
                        Join Maajja
                    </a>
                </div>

                <button
                    className="lg:hidden text-white p-2"
                    onClick={() => setOpen(!open)}
                    data-testid="nav-mobile-toggle"
                    aria-label="Toggle menu"
                >
                    {open ? <X size={24} /> : <Menu size={24} />}
                </button>
            </div>

            {/* Mobile menu */}
            {open && (
                <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="lg:hidden absolute top-[calc(100%+8px)] left-4 right-4 bg-[#030023]/95 backdrop-blur-xl border border-white/15 rounded-[22px] px-6 py-6"
                    data-testid="nav-mobile-menu"
                >
                    <nav className="flex flex-col gap-4">
                        {NAV_ITEMS.map((item) => (
                            <a
                                key={item.label}
                                href={item.href}
                                onClick={() => setOpen(false)}
                                className="text-white/80 hover:text-white py-2"
                                data-testid={`nav-mobile-${item.label.replace(
                                    /\s/g,
                                    "-",
                                )}`}
                            >
                                {item.label}
                            </a>
                        ))}
                        <a
                            href="#cta"
                            onClick={() => setOpen(false)}
                            className="mt-4 text-center bg-[#791EE2] text-white px-6 py-3 rounded-full font-semibold"
                            data-testid="nav-mobile-join"
                        >
                            Join Maajja
                        </a>
                    </nav>
                </motion.div>
            )}
        </motion.header>
    );
};
